import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import Icon from '../../../components/dashboard/DashboardIcons';
import { toast } from '../../../lib/toast';
import { API, auth, Field, Modal, PrimaryBtn, errMsg, inputCls, fmtDate, SESSION_STATUS_LABELS, SESSION_STATUS_COLORS, StatusPill, StudioEmpty } from './shared';

const emptyForm = () => ({ title: '', date: '', start_time: '', end_time: '', objective: '', is_recurring: false, recurrence_end_date: '' });

function weekRange(base) {
  const d = new Date(base);
  const day = d.getDay();
  const start = new Date(d); start.setDate(d.getDate() - day);
  const end = new Date(start); end.setDate(start.getDate() + 6);
  return [start.toISOString().slice(0, 10), end.toISOString().slice(0, 10)];
}

export default function TrainingSchedulePage() {
  const [params] = useSearchParams();
  const [anchor, setAnchor] = useState(new Date());
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreate, setShowCreate] = useState(params.get('new') === '1');
  const [form, setForm] = useState(emptyForm());
  const [detail, setDetail] = useState(null);
  const [attendance, setAttendance] = useState(null);

  const [from, to] = weekRange(anchor);

  const load = async () => {
    setLoading(true); setError('');
    try {
      const res = await axios.get(`${API}/coach-studio/sessions`, { ...auth(), params: { date_from: from, date_to: to, page_size: 100 } });
      setItems(res.data.items);
    } catch (e) { setError(errMsg(e, 'تعذر تحميل الجدول')); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [from, to]);

  const createSession = async () => {
    if (!form.title.trim() || !form.date) { toast.error('العنوان والتاريخ مطلوبان'); return; }
    try {
      await axios.post(`${API}/coach-studio/sessions`, { ...form, recurrence_end_date: form.is_recurring ? form.recurrence_end_date : undefined }, auth());
      toast.success('تمت جدولة الجلسة'); setShowCreate(false); setForm(emptyForm()); load();
    } catch (e) { toast.error(errMsg(e, 'تعذر الجدولة')); }
  };

  const openDetail = async (s) => {
    setDetail(s); setAttendance(null);
    try { const res = await axios.get(`${API}/coach-studio/sessions/${s.id}/attendance`, auth()); setAttendance(res.data); } catch (e) { /* silent */ }
  };

  const reschedule = async (date) => {
    try { await axios.put(`${API}/coach-studio/sessions/${detail.id}/reschedule`, { date }, auth()); toast.success('تم تغيير الموعد'); setDetail(null); load(); }
    catch (e) { toast.error(errMsg(e, 'تعذر تغيير الموعد')); }
  };

  const cancelSession = async () => {
    const reason = window.prompt('سبب الإلغاء:');
    if (reason === null) return;
    if (!reason.trim()) { toast.error('سبب الإلغاء مطلوب'); return; }
    try { await axios.put(`${API}/coach-studio/sessions/${detail.id}/cancel`, { cancel_reason: reason }, auth()); toast.success('تم إلغاء الجلسة'); setDetail(null); load(); }
    catch (e) { toast.error(errMsg(e, 'تعذر الإلغاء')); }
  };

  const startSession = async () => {
    try { await axios.put(`${API}/coach-studio/sessions/${detail.id}/start`, {}, auth()); toast.success('بدأت الجلسة'); openDetail({ ...detail, status: 'started' }); load(); }
    catch (e) { toast.error(errMsg(e, 'تعذر البدء')); }
  };

  const completeSession = async () => {
    try { await axios.put(`${API}/coach-studio/sessions/${detail.id}/complete`, { notes_after: '', executed_exercise_ids: [] }, auth()); toast.success('اكتملت الجلسة'); setDetail(null); load(); }
    catch (e) { toast.error(errMsg(e, 'تعذر الإكمال')); }
  };

  const deleteSession = async () => {
    if (!window.confirm('حذف هذه الجلسة؟')) return;
    try { await axios.delete(`${API}/coach-studio/sessions/${detail.id}?confirm=true`, auth()); toast.success('تم الحذف'); setDetail(null); load(); }
    catch (e) { toast.error(errMsg(e, 'تعذر الحذف')); }
  };

  const grouped = items.reduce((acc, s) => { (acc[s.date] = acc[s.date] || []).push(s); return acc; }, {});

  return (
    <div className="space-y-4" data-testid="training-schedule-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-bold text-slate-900">جدول التدريبات</h2>
        <div className="flex items-center gap-2">
          <button onClick={() => setAnchor(new Date(new Date(anchor).setDate(anchor.getDate() - 7)))} className="rounded-lg border border-slate-200 px-2 py-1.5 text-xs" data-testid="schedule-prev-week-btn">الأسبوع السابق</button>
          <span className="text-xs text-slate-500">{fmtDate(from)} — {fmtDate(to)}</span>
          <button onClick={() => setAnchor(new Date(new Date(anchor).setDate(anchor.getDate() + 7)))} className="rounded-lg border border-slate-200 px-2 py-1.5 text-xs" data-testid="schedule-next-week-btn">الأسبوع التالي</button>
          <PrimaryBtn onClick={() => setShowCreate(true)} data-testid="create-session-btn"><Icon name="plus" className="w-4 h-4" /> جلسة جديدة</PrimaryBtn>
        </div>
      </div>

      {loading ? <div className="p-8 text-center text-sm text-slate-400" data-testid="schedule-loading">جاري التحميل...</div> :
        error ? <div className="p-8 text-center text-sm text-red-500" data-testid="schedule-error">{error}</div> :
        items.length === 0 ? <StudioEmpty text="لا توجد جلسات هذا الأسبوع" icon={<Icon name="calendar" className="w-6 h-6" />} /> : (
        <div className="space-y-3" data-testid="schedule-list">
          {Object.keys(grouped).sort().map((date) => (
            <div key={date} className="rounded-2xl border border-slate-200/70 bg-white p-3 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
              <h4 className="mb-2 text-xs font-bold text-slate-500">{fmtDate(date)}</h4>
              {grouped[date].map((s) => (
                <div key={s.id} onClick={() => openDetail(s)} className="flex cursor-pointer items-center justify-between border-t border-slate-100 py-2 text-sm" data-testid={`session-row-${s.id}`}>
                  <span className="text-slate-700">{s.title} {s.start_time && `— ${s.start_time}`}</span>
                  <StatusPill status={s.status} labels={SESSION_STATUS_LABELS} colors={SESSION_STATUS_COLORS} />
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      <Modal open={showCreate} title="جلسة تدريب جديدة" onClose={() => setShowCreate(false)} wide>
        <div className="space-y-3">
          <Field label="عنوان الجلسة" required><input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} data-testid="session-title-input" /></Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="التاريخ" required><input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={inputCls} data-testid="session-date-input" /></Field>
            <Field label="وقت البداية"><input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className={inputCls} data-testid="session-start-time-input" /></Field>
            <Field label="وقت النهاية"><input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} className={inputCls} data-testid="session-end-time-input" /></Field>
          </div>
          <Field label="الهدف"><textarea value={form.objective} onChange={(e) => setForm({ ...form, objective: e.target.value })} className={inputCls} rows={2} data-testid="session-objective-input" /></Field>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.is_recurring} onChange={(e) => setForm({ ...form, is_recurring: e.target.checked })} data-testid="session-recurring-checkbox" /> جلسة متكررة أسبوعياً
          </label>
          {form.is_recurring && <Field label="تكرار حتى تاريخ"><input type="date" value={form.recurrence_end_date} onChange={(e) => setForm({ ...form, recurrence_end_date: e.target.value })} className={inputCls} data-testid="session-recurrence-end-input" /></Field>}
          <PrimaryBtn onClick={createSession} data-testid="session-submit-btn">جدولة الجلسة</PrimaryBtn>
        </div>
      </Modal>

      <Modal open={!!detail} title={detail?.title} onClose={() => setDetail(null)} wide>
        {detail && (
          <div className="space-y-3" data-testid="session-detail-modal">
            <StatusPill status={detail.status} labels={SESSION_STATUS_LABELS} colors={SESSION_STATUS_COLORS} />
            <p className="text-sm text-slate-600">{detail.objective}</p>
            {attendance && (
              <div className="rounded-lg border border-slate-200 p-3 text-xs" data-testid="session-attendance-info">
                <p>الحاضرون: {attendance.present} / {attendance.total_active_players}</p>
                {attendance.inferred && <p className="text-amber-600">حضور مستنتج من التاريخ والفئة — غير مؤكد بربط مباشر بالجلسة</p>}
              </div>
            )}
            {detail.status !== 'completed' && detail.status !== 'cancelled' && (
              <Field label="تغيير الموعد إلى"><input type="date" onChange={(e) => e.target.value && reschedule(e.target.value)} className={inputCls} data-testid="session-reschedule-input" /></Field>
            )}
            <div className="flex flex-wrap gap-2">
              {detail.status === 'scheduled' && <button onClick={startSession} className="rounded-lg border border-teal-200 px-3 py-1.5 text-xs text-teal-700" data-testid="session-start-btn">بدء الجلسة</button>}
              {(detail.status === 'started' || detail.status === 'scheduled') && <button onClick={completeSession} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs" data-testid="session-complete-btn">إكمال الجلسة</button>}
              {detail.status !== 'completed' && detail.status !== 'cancelled' && <button onClick={cancelSession} className="rounded-lg border border-red-200 px-3 py-1.5 text-xs text-red-600" data-testid="session-cancel-btn">إلغاء</button>}
              <button onClick={deleteSession} className="rounded-lg border border-red-200 px-3 py-1.5 text-xs text-red-600" data-testid="session-delete-btn">حذف</button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
